import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, DatePicker, Select, Space, message, Tag, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axiosInstance from '../../services/axiosConfig';
import { DEPOSIT_API } from '../../constants/api';
import moment from 'moment';

const { TextArea } = Input;

const DepositManagement = ({ contractId, contractData }) => {
  const [form] = Form.useForm();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Format tiền tệ VND
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(amount);
  };

  // Format ngày giờ
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return moment(dateString).format('DD/MM/YYYY');
  };

  // Lấy danh sách tiền đặt cọc
  const fetchDeposits = async () => {
    if (!contractId) return;
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(DEPOSIT_API.GET_BY_CONTRACT(contractId));
      setDeposits(response.data || []);
    } catch (error) {
      console.error('Lỗi khi tải danh sách tiền đặt cọc:', error);
      message.error('Không thể tải danh sách tiền đặt cọc');
    } finally {
      setLoading(false);
    }
  };

  // Lấy lịch sử thay đổi tiền đặt cọc
  const fetchDepositHistory = async () => {
    if (!contractId) return;
    
    setHistoryLoading(true);
    try {
      const response = await axiosInstance.get(DEPOSIT_API.PAYMENT_HISTORY(contractId));
      setHistoryData(response.data || []);
    } catch (error) {
      console.error('Lỗi khi tải lịch sử tiền đặt cọc:', error);
      message.error('Không thể tải lịch sử tiền đặt cọc');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (contractId) {
      fetchDeposits();
    }
  }, [contractId]);

  // Xử lý thêm/cập nhật tiền đặt cọc
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = {
        ContractID: contractId,
        Amount: values.amount,
        PaymentDate: values.depositDate.format('YYYY-MM-DD'),
        PaymentMethod: values.paymentMethod,
        Notes: values.description,
        Status: values.status || 'pending',
        ReceiptNumber: values.receiptNumber
      };

      if (editingDeposit) {
        // Cập nhật đặt cọc
        await axiosInstance.put(DEPOSIT_API.UPDATE(editingDeposit.DepositID), formData);
        message.success('Cập nhật tiền đặt cọc thành công');
      } else {
        // Thêm mới đặt cọc
        await axiosInstance.post(DEPOSIT_API.CREATE, formData);
        message.success('Thêm tiền đặt cọc thành công');
      }

      setVisible(false);
      form.resetFields();
      setEditingDeposit(null);
      fetchDeposits();
    } catch (error) {
      console.error('Lỗi khi lưu tiền đặt cọc:', error);
      message.error('Không thể lưu tiền đặt cọc');
    }
  };

  // Xử lý xóa tiền đặt cọc
  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(DEPOSIT_API.DELETE(id));
      message.success('Xóa tiền đặt cọc thành công');
      fetchDeposits();
    } catch (error) {
      console.error('Lỗi khi xóa tiền đặt cọc:', error);
      message.error('Không thể xóa tiền đặt cọc');
    }
  };

  // Mở modal để chỉnh sửa
  const handleEdit = (record) => {
    setEditingDeposit(record);
    form.setFieldsValue({
      amount: record.Amount,
      depositDate: moment(record.PaymentDate),
      paymentMethod: record.PaymentMethod,
      description: record.Notes,
      status: record.Status,
      receiptNumber: record.ReceiptNumber
    });
    setVisible(true);
  };

  // Mở modal để thêm mới
  const handleAdd = () => {
    setEditingDeposit(null);
    form.resetFields();
    form.setFieldsValue({
      depositDate: moment(),
      status: 'pending',
      paymentMethod: 'Chuyển khoản ngân hàng'
    });
    setVisible(true);
  };

  // Xem lịch sử
  const handleViewHistory = () => {
    setHistoryVisible(true);
    fetchDepositHistory();
  };

  // Cột dữ liệu cho bảng tiền đặt cọc
  const columns = [
    {
      title: 'Ngày đặt cọc',
      dataIndex: 'PaymentDate',
      key: 'PaymentDate',
      render: (date) => formatDate(date),
    },
    {
      title: 'Số tiền',
      dataIndex: 'Amount',
      key: 'Amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Phương thức',
      dataIndex: 'PaymentMethod',
      key: 'PaymentMethod',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      render: (status) => {
        const statusConfig = {
          pending: { color: 'orange', text: 'Chờ xử lý' },
          paid: { color: 'green', text: 'Đã thanh toán' },
          refunded: { color: 'blue', text: 'Đã hoàn trả' },
        };
        return <Tag color={statusConfig[status]?.color || 'default'}>{statusConfig[status]?.text || status}</Tag>;
      }
    },
    {
      title: 'Mô tả',
      dataIndex: 'Notes',
      key: 'Notes',
      ellipsis: true,
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => Modal.confirm({
              title: 'Xác nhận xóa',
              content: 'Bạn có chắc chắn muốn xóa khoản đặt cọc này?',
              onOk: () => handleDelete(record.DepositID),
            })}
          />
        </Space>
      ),
    },
  ];

  // Cột dữ liệu cho bảng lịch sử đặt cọc
  const historyColumns = [
    {
      title: 'Ngày thanh toán',
      dataIndex: 'PaymentDate',
      key: 'PaymentDate',
      render: (date) => formatDate(date),
    },
    {
      title: 'Loại',
      dataIndex: 'Type',
      key: 'Type',
      render: (type) => {
        const typeConfig = {
          payment: { color: 'green', text: 'Thanh toán' },
          refund: { color: 'blue', text: 'Hoàn trả' },
        };
        return <Tag color={typeConfig[type]?.color || 'default'}>{typeConfig[type]?.text || type}</Tag>;
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'Amount',
      key: 'Amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Phương thức',
      dataIndex: 'PaymentMethod',
      key: 'PaymentMethod',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'Notes',
      key: 'Notes',
      ellipsis: true,
    },
  ];

  // Tính tổng tiền đặt cọc
  const totalDeposit = deposits.reduce((total, item) => total + (item.Amount || 0), 0);

  return (
    <Card 
      title="Quản lý tiền đặt cọc" 
      extra={
        <Space>
          <Tooltip title="Xem lịch sử thay đổi">
            <Button 
              icon={<InfoCircleOutlined />} 
              onClick={handleViewHistory}
            >
              Lịch sử
            </Button>
          </Tooltip>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
          >
            Thêm đặt cọc
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <strong>Tổng tiền đặt cọc: {formatCurrency(totalDeposit)}</strong>
      </div>

      <Table 
        dataSource={deposits} 
        columns={columns}
        rowKey="DepositID"
        pagination={false}
        loading={loading}
        locale={{ emptyText: 'Không có dữ liệu tiền đặt cọc' }}
      />

      {/* Modal thêm/sửa tiền đặt cọc */}
      <Modal
        title={editingDeposit ? 'Cập nhật tiền đặt cọc' : 'Thêm tiền đặt cọc'}
        open={visible}
        onOk={handleSubmit}
        onCancel={() => {
          setVisible(false);
          setEditingDeposit(null);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="amount"
            label="Số tiền đặt cọc"
            rules={[{ required: true, message: 'Vui lòng nhập số tiền đặt cọc' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000000}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="Nhập số tiền đặt cọc"
            />
          </Form.Item>

          <Form.Item
            name="depositDate"
            label="Ngày đặt cọc"
            rules={[{ required: true, message: 'Vui lòng chọn ngày đặt cọc' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="paymentMethod"
            label="Phương thức thanh toán"
            rules={[{ required: true, message: 'Vui lòng chọn phương thức thanh toán' }]}
          >
            <Select placeholder="Chọn phương thức thanh toán">
              <Select.Option value="Chuyển khoản ngân hàng">Chuyển khoản ngân hàng</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="receiptNumber"
            label="Số biên lai"
            hidden={true}
          >
            <Input hidden={true} placeholder="Nhập số biên lai" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select placeholder="Chọn trạng thái">
              <Select.Option value="pending">Chờ xử lý</Select.Option>
              <Select.Option value="paid">Đã thanh toán</Select.Option>
              <Select.Option value="refunded">Đã hoàn trả</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <TextArea rows={4} placeholder="Nhập mô tả chi tiết" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal xem lịch sử */}
      <Modal
        title="Lịch sử tiền đặt cọc"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          dataSource={historyData}
          columns={historyColumns}
          rowKey="PaymentID"
          loading={historyLoading}
          locale={{ emptyText: 'Không có dữ liệu lịch sử' }}
        />
      </Modal>
    </Card>
  );
};

export default DepositManagement; 